/**
 * A boolean that remembers itself, per browser, across reloads.
 *
 * Built for the agent chat's two side panels: they should open the way you
 * last left them rather than resetting on every load, but a default is still
 * needed for the first visit and for a browser that will not store anything.
 *
 * 🔴 Every access is guarded. localStorage throws outright in some contexts —
 * a private window with site data blocked, an embedded frame with third-party
 * storage disabled — and it is never worth failing a page render over a
 * remembered panel. A read that cannot happen yields the fallback; a write
 * that cannot happen is dropped and the value still changes on screen.
 */
import { useCallback, useState } from 'react'

function read(key: string, fallback: boolean): boolean {
  try {
    const raw = window.localStorage.getItem(key)
    if (raw === null) return fallback
    return raw === 'true'
  } catch {
    return fallback
  }
}

export default function usePersistedFlag(
  key: string,
  fallback: boolean,
): [boolean, (next: boolean) => void] {
  // Read once, on mount. Re-reading on every render would let another tab's
  // write yank a panel open or shut mid-session, which is not what "remember
  // what I left open" means.
  const [value, setValue] = useState(() => read(key, fallback))

  const set = useCallback((next: boolean) => {
    setValue(next)
    try {
      window.localStorage.setItem(key, String(next))
    } catch {
      // Storage is unavailable; the flag still works for this page's lifetime.
    }
  }, [key])

  return [value, set]
}
